import React, { useState, useEffect, useRef } from 'react';
import './SongEditor.css';
import SongSection from './SongSection';
import ChordDiagram from './ChordDiagram';
import Footer from './Footer';

// --- DYNAMIC URL SETUP ---
const API_SONGS = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api/songs` 
  : 'http://localhost:8000/api/songs';

const API_CHORDS = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api/chords` 
  : 'http://localhost:8000/api/chords';

const SongEditor = ({ initialSong, onBack, token, onNavigate }) => {
  const [currentSong, setCurrentSong] = useState(initialSong);
  const [chordLibrary, setChordLibrary] = useState({});
  const [viewingChordName, setViewingChordName] = useState(null);
  const [variationIndex, setVariationIndex] = useState(0);

  // --- AUTO SCROLL STATE ---
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollSpeed, setScrollSpeed] = useState(5);
  const scrollContainerRef = useRef(null); 
  const scrollSpeedRef = useRef(scrollSpeed);
  const autoScrollPositionRef = useRef(0);

  // --- SAVE STATE ---
  const [saveStatus, setSaveStatus] = useState('saved');
  const [saveError, setSaveError] = useState('');
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    fetch(API_CHORDS).then(res => res.json()).then(setChordLibrary).catch(console.error);
  }, []);

  // --- CORE SAVE FUNCTION ---
  const performSave = async (songData) => {
    setSaveStatus('saving');
    setSaveError('');
    const method = songData._id ? 'PUT' : 'POST';
    const url = songData._id ? `${API_SONGS}/${songData._id}` : API_SONGS;

    try {
      const res = await fetch(url, {
        method: method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(songData)
      });
      
      if (res.ok) {
        const savedData = await res.json();
        if (!songData._id) setCurrentSong(savedData);
        setSaveStatus('saved');
      } else {
        const data = await res.json().catch(() => ({}));
        setSaveError(data.message || 'Your plan does not include this action.');
        setSaveStatus('error');
      }
    } catch (err) { 
        setSaveStatus('error');
        setSaveError('Connection error. Please try again.');
        console.error(err); 
    }
  };

  // --- AUTO-SAVE TRIGGER ---
  const triggerAutoSave = (updatedData) => {
    setCurrentSong(updatedData);
    setSaveStatus('unsaved');
    setSaveError('');
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    
    // Wait 1.5 seconds of inactivity before saving
    saveTimeoutRef.current = setTimeout(() => {
      performSave(updatedData);
    }, 1500);
  };

  // --- DELETE ---
  const handleDelete = async () => {
    if (!currentSong._id || !window.confirm("Are you sure you want to delete this song? This cannot be undone.")) return;
    try { 
      await fetch(`${API_SONGS}/${currentSong._id}`, { 
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      }); 
      onBack(); 
    } catch (err) { console.error(err); }
  };

  // --- DATA UPDATERS ---
  const updateSection = (index, u) => {
    const newSections = [...currentSong.sections];
    newSections[index] = u;
    triggerAutoSave({ ...currentSong, sections: newSections });
  };
  const removeSection = (index) => {
    if(!window.confirm("Remove this section?")) return;
    const newSections = currentSong.sections.filter((_, i) => i !== index);
    triggerAutoSave({ ...currentSong, sections: newSections });
  };
  const addSection = () => {
    triggerAutoSave({ ...currentSong, sections: [...currentSong.sections, { title: "New Section", content: "", strumming: "" }] });
  };
  const moveSection = (index, dir) => {
    const newSections = [...currentSong.sections];
    if (dir === -1 && index === 0) return;
    if (dir === 1 && index === newSections.length - 1) return;
    const target = index + dir;
    [newSections[index], newSections[target]] = [newSections[target], newSections[index]];
    triggerAutoSave({ ...currentSong, sections: newSections });
  };
  const updateField = (field, value) => {
    triggerAutoSave({ ...currentSong, [field]: value });
  };

  // --- SCROLL ENGINE ---
  useEffect(() => {
    scrollSpeedRef.current = scrollSpeed;
  }, [scrollSpeed]);

  useEffect(() => {
    let animationFrameId;
    let lastFrameTime = null;
    const scrollStep = (timestamp) => {
      if (isScrolling && scrollContainerRef.current) {
        if (lastFrameTime === null) {
          autoScrollPositionRef.current = scrollContainerRef.current.scrollTop;
        }
        if (lastFrameTime !== null) {
          const elapsedSeconds = (timestamp - lastFrameTime) / 1000;
          const pixelsPerSecond = 12 + ((scrollSpeedRef.current - 1) * 9);
          autoScrollPositionRef.current += pixelsPerSecond * elapsedSeconds;
          scrollContainerRef.current.scrollTop = autoScrollPositionRef.current;
        }
        lastFrameTime = timestamp;
      }
      animationFrameId = requestAnimationFrame(scrollStep);
    };
    if (isScrolling) animationFrameId = requestAnimationFrame(scrollStep);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isScrolling]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        const activeElement = document.activeElement;
        const tag = activeElement.tagName;
        const isTextField = tag === 'TEXTAREA' || (tag === 'INPUT' && activeElement.type !== 'range');
        if (isTextField) return;
        e.preventDefault();
        e.stopPropagation();
        activeElement?.blur?.();
        setIsScrolling(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, []);

  // --- HELPERS ---
  const getUniqueChords = () => {
    if (!currentSong) return [];
    const allText = currentSong.sections.map(s => s.content || "").join(" ");
    const matches = allText.match(/\[(.*?)\]/g);
    if (!matches) return [];
    
    // FIX: Removed unnecessary escape character to satisfy linter
    const chords = matches.map(c => c.replace(/[[\]]/g, ''));
    return [...new Set(chords)]; 
  };
  
  const openChordModal = (chordName) => { setViewingChordName(chordName); setVariationIndex(0); };
  const nextVariation = () => { const vars = chordLibrary[viewingChordName]; if (!vars) return; setVariationIndex((prev) => (prev + 1) % vars.length); };
  const prevVariation = () => { const vars = chordLibrary[viewingChordName]; if (!vars) return; setVariationIndex((prev) => (prev - 1 + vars.length) % vars.length); };

  const renderSaveIcon = () => {
    if (saveStatus === 'saving') return <div className="save-icon spinning">↻</div>;
    if (saveStatus === 'saved') return <div className="save-icon success">✓</div>;
    if (saveStatus === 'error') return <div className="save-icon error">!</div>;
    return <div className="save-icon unsaved">●</div>;
  };

  return (
    <div className="editor-container">
      <div className="editor-main" ref={scrollContainerRef}>
        
        {/* TOP BAR */}
        <div className="top-bar-sticky">
            <div style={{display:'flex', alignItems:'center', gap:'15px'}}>
                <button className="back-btn" onClick={onBack}>← Back</button>
                <div className="save-status-indicator" title={saveError || (saveStatus === 'saved' ? "All changes saved" : "Saving...")}>
                    {renderSaveIcon()}
                    <span className="save-text">
                        {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : saveStatus === 'error' ? 'Save Error' : 'Unsaved Changes'}
                    </span>
                </div>
            </div>
            
            <div className="scroll-controls-top">
                <button 
                  className={`btn-scroll-toggle ${isScrolling ? 'active' : ''}`}
                  onClick={() => setIsScrolling(!isScrolling)}
                >
                  {isScrolling ? "⏸" : "▶"}
                </button>
                
                <div className="speed-control-group">
                  <span className="speed-label">Speed: {scrollSpeed}</span>
                  <input 
                    type="range" 
                    min="1" max="10" step="1"
                    value={scrollSpeed}
                    onChange={(e) => setScrollSpeed(parseInt(e.target.value))}
                    onPointerUp={(e) => e.currentTarget.blur()}
                    onKeyUp={(e) => {
                      if (e.code === 'Space') e.currentTarget.blur();
                    }}
                    className="speed-slider"
                  />
                </div>
            </div>
        </div>

        {saveError && (
          <div className="save-error-banner">
            Error: {saveError}
          </div>
        )}

        <div className="song-content-wrapper">
            <input 
                className="song-title-large" 
                value={currentSong.title} 
                onChange={(e) => updateField('title', e.target.value)} 
                placeholder="Song Title" 
            />
            <input 
                className="artist-input" 
                value={currentSong.artist} 
                onChange={(e) => updateField('artist', e.target.value)} 
                placeholder="Artist Name" 
            />

            <div className="song-sections-container">
            {currentSong.sections.map((section, index) => (
                <SongSection
                key={index}
                index={index}
                totalSections={currentSong.sections.length}
                section={section}
                onUpdate={(u) => updateSection(index, u)}
                onDelete={() => removeSection(index)}
                onMove={(dir) => moveSection(index, dir)}
                />
            ))}
            </div>

            <button className="btn btn-secondary" onClick={addSection}>+ Add Section</button>
            <div style={{height: '100px'}}></div>

            <Footer onNavigate={onNavigate} />
        </div>
      </div>

      {/* RIGHT SIDEBAR */}
      <div className="sidebar-right">
        <h3>Song Settings</h3>
        
        <div className="tool-panel">
          <label className="tool-label">Key</label>
          <input className="tool-input" value={currentSong.key} onChange={(e) => updateField('key', e.target.value)} />
        </div>
        
        <div className="tool-panel">
          <label className="tool-label">BPM</label>
          <input className="tool-input" type="number" value={currentSong.bpm || ""} onChange={(e) => updateField('bpm', e.target.value)} />
        </div>

        <div className="tool-panel">
          <label className="tool-label">Chords Used</label>
          <div style={{display: 'flex', flexWrap: 'wrap'}}>
            {getUniqueChords().length > 0 ? (
              getUniqueChords().map((chord, i) => (
                <span key={i} className="chord-badge" onClick={() => openChordModal(chord)} style={{cursor: 'pointer'}}>{chord}</span>
              ))
            ) : ( <span style={{color:'#999', fontSize:'0.8rem'}}>No chords yet</span> )}
          </div>
        </div>

        <div className="tool-panel" style={{marginTop: '40px', borderColor: '#ffcccc'}}>
            <label className="tool-label" style={{color: '#dc3545'}}>Danger Zone</label>
            <button className="btn btn-danger" onClick={handleDelete} style={{width:'100%', marginTop:'5px'}}>Delete Song</button>
        </div>

      </div>

      {/* CHORD MODAL */}
      {viewingChordName && (
        <div className="chord-modal-overlay" onClick={() => setViewingChordName(null)}>
          <div className="chord-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 style={{marginBottom: '10px', fontSize: '1.5rem', color: '#333'}}>{viewingChordName}</h3>
            <div className="chord-placeholder">
              {chordLibrary[viewingChordName] ? (
                <div>
                   {chordLibrary[viewingChordName].length > 1 && (
                     <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                       <button className="btn-arrow" onClick={prevVariation}>&lt;</button>
                       <span style={{fontSize: '0.8rem', color: '#888'}}>Var {variationIndex + 1} / {chordLibrary[viewingChordName].length}</span>
                       <button className="btn-arrow" onClick={nextVariation}>&gt;</button>
                     </div>
                   )}
                  <div style={{ margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                    <ChordDiagram chordData={chordLibrary[viewingChordName][variationIndex]} name="" />
                  </div>
                </div>
              ) : ( <p>Chord not found in library.</p> )}
            </div>
            <button className="btn btn-secondary" onClick={() => setViewingChordName(null)} style={{marginTop: '15px'}}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SongEditor;
